import classNames from "classnames";
import { CalendarMode, CalendarView, RangeModeTypes } from "PFApp/booking/types";
import { Button } from "PFComponents/button";
import { Icon } from "PFComponents/icon";
import { Select } from "PFComponents/select/select";
import { useDateFormatter } from "PFCore/hooks/use_date_formatter";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { RangeMode } from "../bookings_view/bookings_view";
import { useBookingsOverviewContext } from "../context/bookings_overview_context";
import { useCalendarContext } from "../context/calendar_context";
import { CONTROLS_HEIGHT, FILTERS_ROW_HEIGHT } from "./calendar";
import { getModeDays } from "./calendar.utils";
import css from "./controls.module.scss";
import { GranularitySelector } from "./granularity_selector";

const rangeOptions = [
  {
    id: RangeModeTypes.Day,
    item: RangeModeTypes.Day,
    displayElementKey: "day"
  },
  {
    id: RangeModeTypes.Week,
    item: RangeModeTypes.Week,
    displayElementKey: "week"
  },
  {
    id: RangeModeTypes.TwoWeeks,
    item: RangeModeTypes.TwoWeeks,
    displayElementKey: "2weeks"
  },
  {
    id: RangeModeTypes.Month,
    item: RangeModeTypes.Month,
    displayElementKey: "month"
  },
  {
    id: RangeModeTypes.Quarter,
    item: RangeModeTypes.Quarter,
    displayElementKey: "quarter"
  }
] as const;

interface IProps {
  range: { start: string; end: string };
  rangeMode: RangeMode;
  onRangeModeChange: (value: RangeMode) => void;
  onTodayClick: () => void;
  isTodayDisabled: boolean;
  onDirectionClick: (value: number) => void;
  isDirectionDisabled: (value: number) => boolean;
}

const Controls = ({
  range,
  rangeMode,
  onRangeModeChange,
  onTodayClick,
  isTodayDisabled,
  onDirectionClick,
  isDirectionDisabled
}: IProps) => {
  const {
    calendarMode,
    calendarData: { view }
  } = useBookingsOverviewContext();
  const { dayWidth } = useCalendarContext();
  const { formatDate, utc } = useDateFormatter();
  const { t } = useTranslation(["bookingModule", "translation"]);

  const options = useMemo(
    () => [
      ...rangeOptions.map((i) => ({
        ...i,
        displayElement: t(`bookingModule:calendar.options.${i.displayElementKey}`)
      })),
      ...(rangeMode.type === RangeModeTypes.Custom
        ? [
            {
              id: RangeModeTypes.Custom,
              item: RangeModeTypes.Custom,
              displayElement: t("bookingModule:calendar.options.custom")
            }
          ]
        : [])
    ],
    [rangeMode, t]
  );

  const selectedIndex = options.findIndex((i) => i.id === rangeMode.type);
  const formattedRange = `${formatDate(utc(range.start))} - ${formatDate(utc(range.end))}`;
  const scrollStep = dayWidth * getModeDays(rangeMode, utc);
  const prevDisabled = isDirectionDisabled(-scrollStep);
  const nextDisabled = isDirectionDisabled(scrollStep);
  const shouldDisplayGranularitySelector =
    calendarMode === CalendarMode.Workforce &&
    ![CalendarView.BookingsOnly, CalendarView.Compressed].includes(view);

  return (
    <div style={{ height: CONTROLS_HEIGHT, top: FILTERS_ROW_HEIGHT }} className={css.container}>
      <div className={css.todayContainer}>
        <Button
          kind="secondary"
          text={t("translation:today")}
          disabled={isTodayDisabled}
          onClick={onTodayClick}
        />
      </div>
      <div className={css.range}>
        <Button
          kind="secondary"
          icon="chevron-left"
          disabled={prevDisabled}
          onClick={() => onDirectionClick(-scrollStep)}
        />
        <span className={css.rangeText}>{formattedRange}</span>
        <Button
          disabled={nextDisabled}
          icon="chevron-right"
          kind="secondary"
          onClick={() => onDirectionClick(scrollStep)}
        />
      </div>
      <div className={css.granularityContainer}>
        {shouldDisplayGranularitySelector && <GranularitySelector />}
        <Select
          //@ts-ignore
          kind="bordered"
          readOnly
          className={classNames(css.selectRange)}
          inputInnerStyle={{
            display: "flex",
            alignItems: "center",
            paddingLeft: 5
          }}
          handleChange={(type) => onRangeModeChange({ type })}
          displayValues={
            <div className={css.rangeValue}>
              <Icon name="calendar" size="md" />
              {options.find(({ id }) => id === rangeMode.type)?.displayElement}
            </div>
          }
          options={options}
          selectedIndex={selectedIndex}
          controlledValue
        />
      </div>
    </div>
  );
};

export default Controls;
